import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Grid,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PolicyIcon from '@material-ui/icons/Policy';

const PolicyRouterResponseCard = ({ routerResponse, classes }) => {
  const [expandedPolicies, setExpandedPolicies] = useState({});

  if (!routerResponse?.dataJson?.policies) return null;

  const policies = routerResponse.dataJson.policies;

  const handlePolicyExpand = (policyNumber) => {
    setExpandedPolicies(prev => ({
      ...prev,
      [policyNumber]: !prev[policyNumber],
    }));
  };

  const renderPolicyInfo = (policy) => {
    const policyInfo = policy.PolicyInfo || policy.policyInfo;
    if (!policyInfo) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Full Policy Number:</strong> {policyInfo.FullPolicyNumber || policyInfo.fullPolicyNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Client Number:</strong> {policyInfo.ClientNumber || policyInfo.clientNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Program:</strong> {policyInfo.ProgramNumber || policyInfo.programNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Product Code:</strong> {policyInfo.ProductCode || policyInfo.productCode}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Effective Date:</strong> {policyInfo.EffectiveDate || policyInfo.effectiveDate}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Expiration Date:</strong> {policyInfo.ExpirationDate || policyInfo.expirationDate}
            </Typography>
          </Grid>
          {(policyInfo.CancellationDate || policyInfo.cancellationDate) && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Cancellation Date:</strong> {policyInfo.CancellationDate || policyInfo.cancellationDate}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Policy Limits */}
        {(policyInfo.PolicyLimits || policyInfo.policyLimits) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Policy Limits: </strong>
              {policyInfo.PolicyLimits?.Type && (
              <Chip
                label={`${policyInfo.PolicyLimits?.Type} - ${policyInfo.PolicyLimits?.Value} ${policyInfo.PolicyLimits?.Unit}`}
                size="small"
                color={'primary'}
              />
              )}
            </Typography>
          </Box>
        )}

        {/* Services */}
        {(policyInfo.Services || policyInfo.services) && (policyInfo.Services || policyInfo.services).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Services
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Service Class</strong></TableCell>
                  <TableCell><strong>Coverage</strong></TableCell>
                  <TableCell><strong>Limit Value</strong></TableCell>
                  <TableCell><strong>Limit Unit</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(policyInfo.Services || policyInfo.services).map((service, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{service.ServiceClass || service.serviceClass}</TableCell>
                    <TableCell>
                      <Chip
                        label={service.Coverage || service.coverage}
                        size="small"
                        color={(service.Coverage || service.coverage) === 'Y' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{service.LimitInfo?.Value || service.limitInfo?.value}</TableCell>
                    <TableCell>{service.LimitInfo?.Unit || service.limitInfo?.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  const renderVehicleInfo = (vehicleArray) => {
    if (!vehicleArray || !Array.isArray(vehicleArray) || vehicleArray.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          <strong>Vehicle Info</strong>
        </Typography>
        {vehicleArray.map((vehicle, vIdx) => (
          <Box key={vIdx} sx={{ ml: 1, mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Make:</strong> {vehicle.make || vehicle.Make}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Model:</strong> {vehicle.model || vehicle.Model}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Year:</strong> {vehicle.year || vehicle.Year}
                </Typography>
              </Grid>
              {(vehicle.plate || vehicle.Plate) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Plate:</strong> {vehicle.plate || vehicle.Plate}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>VIN8:</strong> {vehicle.vin?.vin8 || vehicle.VIN?.VIN8}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Full VIN:</strong> {vehicle.vin?.fullvin || vehicle.VIN?.FULLVIN}
                </Typography>
              </Grid>

              {/* Coverages */}
              {(vehicle.coverages || vehicle.Coverages) && (vehicle.coverages || vehicle.Coverages).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
                    <strong>Coverages: </strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(vehicle.coverages || vehicle.Coverages).map((coverage, cIdx) => (
                      <Chip
                        key={cIdx}
                        label={coverage.coverageType || coverage.CoverageType}
                        size="small"
                        color={(coverage.hasCoverage || coverage.HasCoverage) ? 'primary' : 'default'}
                        variant={(coverage.hasCoverage || coverage.HasCoverage) ? 'default' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
      </Box>
    );
  };

  const renderMemberInfo = (memberInfo) => {
    if (!memberInfo) return null;

    return (
     <>
     <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        <strong>Member Info: </strong>
      </Typography>
      <Box sx={{ mt: 2, p: 1, backgroundColor: '#96b3f1', borderRadius: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>First Name:</strong> {memberInfo.FirstName || memberInfo.firstName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Last Name:</strong> {memberInfo.LastName || memberInfo.lastName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Phone:</strong> {memberInfo.PhoneNumber || memberInfo.phoneNumber}
            </Typography>
          </Grid>
          { (memberInfo.Email || memberInfo.email) && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Email:</strong> {memberInfo.Email || memberInfo.email}
            </Typography>
          </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Address:</strong> {memberInfo.Address || memberInfo.address}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>City:</strong> {memberInfo.City || memberInfo.city}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>State:</strong> {memberInfo.State || memberInfo.state}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <strong>Zip:</strong> {memberInfo.Zip || memberInfo.zip}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      </>
    );
  };

  const renderCustomFields = (customFieldsArray) => {
    if (!customFieldsArray || !Array.isArray(customFieldsArray) || customFieldsArray.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          <strong>Custom Fields: </strong>
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customFieldsArray.map((field, idx) => (
              <TableRow key={idx}>
                <TableCell>{field.Name || field.name}</TableCell>
                <TableCell>{field.Value || field.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Card variant="outlined" style={{ marginBottom: '20px', width: '100%' }}>
      <CardHeader
        title="Response from Policy Router"
        avatar={<ArrowBackIcon />}
        className={classes.logSearchDialogHeader}
        subheader={`Status Code: ${routerResponse?.statusCode}`}
      />
      <CardContent>
        {policies.length === 0 ? (
          <Typography variant="body2">No policies found in response.</Typography>
        ) : (
          policies.map((policy, pIdx) => {
            const policyNumber = policy.PolicyInfo?.PolicyNumber || policy.policyInfo?.policyNumber;
            const isExpanded = expandedPolicies[policyNumber];

            return (
            <>
              <Accordion
                key={pIdx}
                expanded={isExpanded}
                onChange={() => handlePolicyExpand(policyNumber)}
                style={{ marginBottom: 12 }}
              >
                <AccordionSummary startIcon={<PolicyIcon />} expandIcon={<ExpandMoreIcon />}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <PolicyIcon style={{ marginRight: 8 }} />
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {policyNumber}
                    </Typography>
                </div>
                </AccordionSummary>
                <AccordionDetails style={{ display: 'block' }}>
                  {renderPolicyInfo(policy)}
                  {renderMemberInfo(policy.MemberInfo || policy.memberInfo)}
                  {renderVehicleInfo(policy.vehicleInfo || policy.VehicleInfo)}
                  {renderCustomFields(policy.PolicyInfo?.CustomFields || policy.policyInfo?.customFields)}
                </AccordionDetails>
              </Accordion>
            </>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

PolicyRouterResponseCard.propTypes = {
  routerResponse: PropTypes.object,
  classes: PropTypes.object.isRequired,
};

export default PolicyRouterResponseCard;
